"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-20 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/30 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList>
        {menuItems.slice(0, 4).map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-4 py-2 text-sm font-medium text-foreground transition-all duration-[80ms] ease-in-out hover:!bg-transparent focus:!bg-transparent">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-0.5 w-0 bg-primary transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] group-hover:left-0 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) {
    return <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" disabled>
        <Menu className="h-5 w-5" />
      </Button>;
  }
  return <>
      <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
        {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {isOpen && <div className="absolute top-full left-0 right-0 bg-background border-t shadow-medium md:hidden">
          <nav className="flex flex-col p-4">
            {menuItems.slice(0, 4).map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="py-3 px-4 text-sm font-medium text-foreground hover:bg-accent rounded-md transition-all duration-[80ms]" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-background border-b transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${isScrolled ? "h-16 shadow-soft" : "h-22"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <Link to="/" className="flex-shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1517610/img/image_q6w72e0c.png" alt="Click Viral" className="h-10" />
        </Link>

        <div className="flex items-center gap-6">
          <MenuProvider menu_id="3" className="flex-1 flex justify-center">
            <HeaderMenuConsumer />
          </MenuProvider>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative">
              {searchOpen ? <div className="flex items-center gap-2">
                  <Input type="search" placeholder="Search..." className="w-48 transition-all duration-[var(--duration-medium)] focus:w-64" autoFocus onBlur={() => setSearchOpen(false)} />
                </div> : <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} className="hover:!bg-transparent focus:!bg-transparent" aria-label="Search">
                  <Search className="h-5 w-5" />
                </Button>}
            </div>

            <Button asChild className="hover:scale-[1.02] hover:shadow-medium hover:bg-primary-hover active:bg-primary-active transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:!bg-primary-hover focus:!bg-transparent">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive text-primary-foreground shadow-xs h-9 px-4 py-2 has-[>svg]:px-3 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] !bg-[hsl(213,94%,68%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:scale-[1.02] hover:shadow-medium hover:bg-primary-hover active:bg-primary-active hover:!bg-primary-hover focus:!bg-transparent">Get Started</Link>
            </Button>
          </div>

          <MenuProvider menu_id="3">
            <MobileMenuConsumer />
          </MenuProvider>
        </div>
      </div>
    </header>;
}